import React, {
  useMemo,
  useState,
  useCallback,
  FC,
}                              from 'react';
import { History }             from 'history';
import styled                  from 'styled-components';
import {
  IOption,
  TDataTableRow,
  ISearchInputValues,
}                              from '@typings/components';
import {
  IAuction,
  IAuctionsResponse,
  IFetchBid,
  TStatusOption,
  TStatusFilterValue,
}                              from '@typings/auction';
import { ISearchQueryParams }  from '@typings/search';
import { EAppPage }            from '@enums/common';
import {
  EAccountPage,
  EAccountSubpage,
}                              from '@enums/accountManagement';
import { EAuctionStatus }      from '@enums/auction';
import {
  useFetchAuctionsStatuses,
  useFetchAuctions,
  useFetchAuctionBidData,
}                              from '@modules/auctions/queries/auctionsQueries';
import {
  capitalize,
  formatTitle,
  formatCurrency,
  formatAuctionDate,
  getRetailPercentage,
  getWholesalePercentage,
  isBeforeNow,
  getIsShowBidList,
  getIsShownDetails,
}                              from '@utils/commonFunctions';
import { useToggle }           from '@utils/useToggle';
import { ROWS_PER_PAGE }       from '@utils/suggestions';
import { AUCTION_STATUS_NAME } from '@modules/auctions/utils/suggestions';
import { BidListModal }        from '@modules/auctions/components/BidListModal';
import {
  LinkButton,
  VinWrapper,
}                              from '@components/index';
import { AuctionsPage }        from '@modules/auctions/pages/AuctionsPage';

const AUCTIONS_STATUS_ORDER: EAuctionStatus[] = [
  EAuctionStatus.Active,
  EAuctionStatus.Ended,
  EAuctionStatus.SavedDraftNoPhotos,
  EAuctionStatus.SavedDraft,
  EAuctionStatus.Canceled,
  EAuctionStatus.PendingNoPhotos,
  EAuctionStatus.Pending,
  EAuctionStatus.Archived,
];

const Wrapper = styled.div`
  padding: 20px 30px;

  @media (min-width: 1280px) {
    padding: 20px 100px;
  }
`;

const TitleColumn = styled.div`
  margin: 0 5px;

  p {
    font-size: 15px;
  }

  span {
    font-size: 12px;
  }
`;

const VinColumn = styled.div`
  p {
    font-size: 12px;
  }

  span {
    font-size: 14px;
  }
`;

export const getTableRows = (
  auctions           : IAuction[],
  push               : (path: string) => void,
  handleBidListModal : (item: IAuction) => void,
): TDataTableRow[] =>
  (auctions ?? []).map(
    (auction: IAuction): TDataTableRow => {
      const wholesalePercentage = getWholesalePercentage(auction);
      const retailPercentage    = getRetailPercentage(auction);
      const isShowBidList       = getIsShowBidList(auction.status);
      const isShownDetailsForm  = getIsShownDetails(auction.status);

      return [
        {
          value: (
            <>
              <TitleColumn>
                <p>{formatTitle(auction)}</p>
                <span>{auction?.creditUnion?.name}</span>
              </TitleColumn>
            </>
          ),
        },
        {
          value: (
            <VinColumn>
              <p>
                {isBeforeNow(auction.endDate) ? 'Ended ' : 'Ends '}
                {formatAuctionDate(auction.endDate, 'MM/DD/YY hh:mm A z')}
              </p>
              <VinWrapper>{auction.vehicle?.vin ?? ''}</VinWrapper>
            </VinColumn>
          ),
        },
        {
          align : 'center',
          value : (
            <>
              {isShowBidList && (
                <LinkButton
                  label   = "Bid List"
                  onClick = {() => handleBidListModal && handleBidListModal(auction)}
                />
              )}

              {isShownDetailsForm && (
                <LinkButton
                  label   = {capitalize(EAccountSubpage.Details)}
                  onClick = {
                    (): void =>
                      push(`${EAppPage.AccountManagement}/${auction.cuid}/${EAccountPage.Auctions}/${auction.id}/${EAccountSubpage.Details}`)
                  }
                />
              )}

              <LinkButton
                label   = {capitalize(EAccountSubpage.Photos)}
                onClick = {
                  (): void => {
                    sessionStorage.setItem(auction.id, `${auction.status}`);

                    push(`${EAppPage.AccountManagement}/${auction.cuid}/${EAccountPage.Auctions}/${auction.id}/${EAccountSubpage.Photos}`);
                  }
                }
              />
            </>
          ),
        },
        {
          value : auction.watches || '—',
          align : 'center',
        },
        {
          value : auction.views || '—',
          align : 'center',
        },
        {
          value : auction.bidsCount || '—',
          align : 'center',
        },
        {
          value : formatCurrency(auction.currentHighestBid),
          align : 'center',
        },
        {
          value : wholesalePercentage ? `${wholesalePercentage}%` : '—',
          align : 'center',
        },
        {
          value : retailPercentage ? `${retailPercentage}%` : '—',
          align : 'center',
        },
      ];
    },
  );

export const AuctionsSearchContainer: FC<Pick<History, 'push'>> = ({ push }) => {
  const [selectedFilter, setSelectedFilter] = useState<TStatusFilterValue>('all');
  const [searchText, setSearchText]         = useState('');
  const [currentPage, setCurrentPage]       = useState(0);

  const [isBidListModal, toggleIsBidListModal] = useToggle();

  const params = useMemo<ISearchQueryParams>(
    () => ({
      skip : currentPage * ROWS_PER_PAGE,
      size : ROWS_PER_PAGE,
      ...(selectedFilter !== 'all' && { filters: `Status eq ${selectedFilter}` }),
      ...(searchText && { searchText }),
    }),
    [currentPage, searchText, selectedFilter],
  );

  // #region API INTEGRATION
  const {
    mutate : fetchBidData,
    data   : currentBid = [] as IFetchBid[],
    isLoading,
  } = useFetchAuctionBidData();

  const {
    data      : filterOptions = [] as TStatusOption[],
    isLoading : isLoadingAuctionStatuses,
  } = useFetchAuctionsStatuses();

  const {
    data      : auctionsResponse = {} as IAuctionsResponse,
    isLoading : isLoadingAuctions,
  } = useFetchAuctions(params);
  // #endregion

  const filterValues = useMemo<IOption[]>(
    ()  => {
      const allAuctionsCount = filterOptions.reduce(
        (acc: number, cur: TStatusOption): number => acc + cur.count,
        0,
      );

      if (!allAuctionsCount) return [];

      const allAuctionsOption = {
        value : 'all',
        label : {
          status : 'Default - All -',
          count  : `(${allAuctionsCount})`,
        },
      };

      const opts = filterOptions.reduce(
        (acc: any, cur: TStatusOption): any => ({
          ...acc,
          [cur.value]: {
            value : cur.value,
            label : {
              status : AUCTION_STATUS_NAME[cur.value],
              count  : `(${cur.count})`,
            },
          },
        }), {},
      );

      return AUCTIONS_STATUS_ORDER.reduce(
        (acc: any, cur: EAuctionStatus): any => {
          if (opts[cur]) {
            return [
              ...acc,
              opts[cur],
            ];
          }

          return acc;
        }, [allAuctionsOption],
      );
    },
    [filterOptions],
  );

  const handleBidListModal = useCallback(
    (auction: IAuction) => {
      fetchBidData(auction?.id);
      toggleIsBidListModal();
    },
    [fetchBidData, toggleIsBidListModal],
  );

  const handleSelectFilterValue = useCallback(
    ({ value }: any): void => {
      setSelectedFilter(value);
      setCurrentPage(0);
    },
    [setSelectedFilter, setCurrentPage],
  );

  const handleSearchText = useCallback(
    ({ searchText: text }: ISearchInputValues): void => {
      setSearchText(text);
      setCurrentPage(0);
    },
    [setSearchText, setCurrentPage],
  );

  const tableRows = useMemo(
    (): TDataTableRow[] => getTableRows(auctionsResponse.auctions, push, handleBidListModal),
    [auctionsResponse, push, handleBidListModal],
  );

  return (
    <Wrapper>
      <AuctionsPage
        inputValue              = {searchText}
        filterValues            = {filterValues}
        selectedFilterValue     = {selectedFilter}
        totalAuctionsCount      = {auctionsResponse?.totalRecordsFound ?? 0}
        tableRows               = {tableRows}
        currentPage             = {currentPage}
        rowsPerPage             = {ROWS_PER_PAGE}
        handleSearch            = {handleSearchText}
        handleChangePage        = {setCurrentPage}
        handleSelectFilterValue = {handleSelectFilterValue}
        isLoading               = {isLoadingAuctions || isLoadingAuctionStatuses}
      />

      <BidListModal
        isOpen      = {isBidListModal}
        isLoading   = {isLoading}
        currentBid  = {currentBid}
        toggleModal = {toggleIsBidListModal}
      />
    </Wrapper>
  );
};
